import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState } from '@/components/shared/states';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate } from '@/utils/format';
import { nationalDisplayFromE164 } from '@/utils/phone';

const DECISION_TYPES = ['salvation', 'rededication', 'baptism', 'other'] as const;

interface SoulWonDetail {
  id: string;
  firstName: string;
  lastName: string;
  personName: string;
  phone: string | null;
  location: string | null;
  wonOn: string;
  decisionType: (typeof DECISION_TYPES)[number];
  notes: string | null;
  followUpId: string | null;
  convertedMemberId: string | null;
}

const decisionBadgeVariant = (
  decisionType: SoulWonDetail['decisionType'],
): BadgeVariant => {
  switch (decisionType) {
    case 'salvation':
      return 'success';
    case 'baptism':
      return 'info';
    case 'rededication':
      return 'brand';
    default:
      return 'neutral';
  }
};

const DetailRow = ({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}) => (
  <div
    className={
      wide
        ? 'grid grid-cols-[8rem_1fr] gap-2 text-body-sm sm:col-span-2 sm:grid-cols-[10rem_1fr]'
        : 'grid grid-cols-[8rem_1fr] gap-2 text-body-sm sm:grid-cols-[10rem_1fr]'
    }
  >
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0 break-words">{value}</dd>
  </div>
);

export const SoulWonDetailPage = () => {
  const { t } = useTranslation('evangelism');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { soulWonId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = usePreferences((state) => state.locale);
  const isNew = soulWonId === 'new';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [wonOn, setWonOn] = useState<string | null>(null);
  const [decisionType, setDecisionType] = useState<(typeof DECISION_TYPES)[number]>('salvation');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [createMember, setCreateMember] = useState(false);
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpFirstName, setFollowUpFirstName] = useState('');
  const [followUpLastName, setFollowUpLastName] = useState('');
  const [followUpPhone, setFollowUpPhone] = useState<string | null>(null);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);

  const detail = useQuery({
    queryKey: ['evangelism', 'souls-won', soulWonId],
    queryFn: () => api.get<SoulWonDetail>(`/evangelism/souls-won/${soulWonId}`),
    enabled: !isNew && Boolean(soulWonId),
  });

  const needsPeopleRecord = createMember || createFollowUp;
  const canSubmit =
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    Boolean(wonOn) &&
    (!needsPeopleRecord || Boolean(phone));

  const record = useMutation({
    mutationFn: () =>
      api.post<SoulWonDetail>('/evangelism/souls-won', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone ?? undefined,
        location: location.trim() || undefined,
        wonOn,
        decisionType,
        notes: notes.trim() || undefined,
        createMember: needsPeopleRecord ? createMember || createFollowUp : false,
        createFollowUp,
      }),
    onSuccess: async (created) => {
      toast.success(t('saved'));
      await queryClient.invalidateQueries({ queryKey: ['evangelism'] });
      navigate(`/evangelism/${created.id}`, { replace: true });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const assignFollowUp = useMutation({
    mutationFn: () =>
      api.post<SoulWonDetail>(`/evangelism/souls-won/${soulWonId}/follow-up`, {
        firstName: followUpFirstName.trim(),
        lastName: followUpLastName.trim(),
        phone: followUpPhone ?? undefined,
      }),
    onSuccess: async () => {
      toast.success(t('saved'));
      setShowFollowUpForm(false);
      await queryClient.invalidateQueries({ queryKey: ['evangelism', 'souls-won', soulWonId] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const row = detail.data;
  const wonOnLabel = row ? formatDate(row.wonOn, { locale, style: 'medium' }) : '';
  const phoneLabel = row?.phone ? nationalDisplayFromE164(row.phone) : null;

  const openFollowUpForm = () => {
    if (!row) return;
    setFollowUpFirstName(row.firstName);
    setFollowUpLastName(row.lastName);
    setFollowUpPhone(row.phone);
    setShowFollowUpForm(true);
  };

  if (!isNew && detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-5 w-96" />
        <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!isNew && detail.isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('detail.notFound')}
          description={t('detail.notFoundBody')}
          breadcrumbs={[
            { label: t('title'), to: '/evangelism' },
            { label: t('detail.title') },
          ]}
        />
        <ErrorState
          title={tCommon('state.errorTitle')}
          body={tCommon('state.errorBody')}
          onRetry={() => void detail.refetch()}
          retryLabel={tCommon('action.retry')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? t('record') : (row?.personName ?? t('detail.title'))}
        description={
          isNew
            ? t('description')
            : row
              ? t('detail.summary', {
                  decision: t(`decisionType.${row.decisionType}`),
                  date: wonOnLabel,
                })
              : undefined
        }
        breadcrumbs={[
          { label: t('title'), to: '/evangelism' },
          { label: isNew ? t('record') : (row?.personName ?? t('detail.title')) },
        ]}
        headerActions={
          row ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={decisionBadgeVariant(row.decisionType)}>
                {t(`decisionType.${row.decisionType}`)}
              </Badge>
              {row.followUpId ? (
                <Badge variant="neutral">{t('status.followUp')}</Badge>
              ) : null}
              {row.convertedMemberId ? (
                <Badge variant="success">{t('status.converted')}</Badge>
              ) : null}
              {!row.followUpId && !row.convertedMemberId ? (
                <Badge variant="neutral">{t('status.open')}</Badge>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {isNew ? (
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <CardTitle>{t('record')}</CardTitle>
            <CardDescription>{t('description')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={tCommon('field.firstName')}>
                {(field) => (
                  <Input
                    id={field.id}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    aria-describedby={field['aria-describedby']}
                    aria-invalid={field.invalid}
                  />
                )}
              </FormField>
              <FormField label={tCommon('field.lastName')}>
                {(field) => (
                  <Input
                    id={field.id}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    aria-describedby={field['aria-describedby']}
                    aria-invalid={field.invalid}
                  />
                )}
              </FormField>
            </div>
            <FormField
              label={tCommon('field.phone')}
              hint={needsPeopleRecord ? t('detail.phoneHint') : undefined}
            >
              {(field) => (
                <PhoneInput
                  id={field.id}
                  value={phone}
                  onChange={setPhone}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                />
              )}
            </FormField>
            <FormField label={t('detail.wonOn')}>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={wonOn}
                  onChange={setWonOn}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                />
              )}
            </FormField>
            <FormField label={t('detail.decision')}>
              {(field) => (
                <Listbox
                  value={decisionType}
                  onChange={(value) => setDecisionType(value as (typeof DECISION_TYPES)[number])}
                  options={DECISION_TYPES.map((value) => ({
                    value,
                    label: t(`decisionType.${value}`),
                  }))}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                />
              )}
            </FormField>
            <FormField label={t('detail.location')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                />
              )}
            </FormField>
            <FormField label={t('detail.notes')}>
              {(field) => (
                <Textarea
                  id={field.id}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                />
              )}
            </FormField>
          </CardContent>
          <CardContent className="flex flex-col gap-4 border-t border-border pt-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="createMember"
                checked={createMember}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setCreateMember(next);
                  if (!next) setCreateFollowUp(false);
                }}
              />
              <Label htmlFor="createMember" className="cursor-pointer text-body-sm leading-snug">
                {t('detail.convert')}
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="createFollowUp"
                checked={createFollowUp}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setCreateFollowUp(next);
                  if (next) setCreateMember(true);
                }}
              />
              <Label htmlFor="createFollowUp" className="cursor-pointer text-body-sm leading-snug">
                {t('detail.assignFollowUp')}
              </Label>
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" asChild>
              <Link to="/evangelism">{tCommon('action.cancel')}</Link>
            </Button>
            <Button disabled={record.isPending || !canSubmit} onClick={() => record.mutate()}>
              {t('record')}
            </Button>
          </CardFooter>
        </Card>
      ) : row ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.about')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 sm:grid-cols-2">
                <DetailRow
                  label={t('detail.wonOn')}
                  value={wonOnLabel}
                />
                <DetailRow
                  label={tCommon('field.phone')}
                  value={phoneLabel ?? t('detail.noPhone')}
                />
                <DetailRow
                  label={t('detail.decision')}
                  value={t(`decisionType.${row.decisionType}`)}
                />
                {row.location ? (
                  <DetailRow label={t('detail.location')} value={row.location} />
                ) : null}
              </dl>
              {row.notes ? (
                <section className="mt-6 border-t border-border pt-4">
                  <h4 className="mb-2 text-body font-medium">{t('detail.notes')}</h4>
                  <p className="whitespace-pre-wrap text-body-sm text-fg-secondary">{row.notes}</p>
                </section>
              ) : null}
            </CardContent>
          </Card>

          <Card className="h-fit">
            <CardHeader>
              <CardTitle>{t('detail.nextSteps')}</CardTitle>
              <CardDescription>
                {row.followUpId
                  ? t('detail.followUpLinked')
                  : row.convertedMemberId
                    ? t('detail.addedToMembers')
                    : t('detail.nextStepsOpen')}
              </CardDescription>
            </CardHeader>
            {showFollowUpForm && !row.followUpId ? (
              <CardContent className="flex flex-col gap-4 border-t border-border pt-4">
                <p className="text-body-sm text-fg-secondary">{t('detail.followUpNameHint')}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label={tCommon('field.firstName')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        value={followUpFirstName}
                        onChange={(e) => setFollowUpFirstName(e.target.value)}
                        aria-describedby={field['aria-describedby']}
                        invalid={field.invalid}
                      />
                    )}
                  </FormField>
                  <FormField label={tCommon('field.lastName')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        value={followUpLastName}
                        onChange={(e) => setFollowUpLastName(e.target.value)}
                        aria-describedby={field['aria-describedby']}
                        invalid={field.invalid}
                      />
                    )}
                  </FormField>
                </div>
                <FormField label={tCommon('field.phone')}>
                  {(field) => (
                    <PhoneInput
                      id={field.id}
                      value={followUpPhone}
                      onChange={setFollowUpPhone}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>
              </CardContent>
            ) : null}
            <CardFooter>
              {showFollowUpForm && !row.followUpId ? (
                <>
                  <Button variant="outline" onClick={() => setShowFollowUpForm(false)}>
                    {tCommon('action.cancel')}
                  </Button>
                  <Button
                    disabled={
                      assignFollowUp.isPending ||
                      !followUpFirstName.trim() ||
                      !followUpLastName.trim() ||
                      !(followUpPhone || row.phone)
                    }
                    onClick={() => assignFollowUp.mutate()}
                  >
                    {t('detail.assignFollowUp')}
                  </Button>
                </>
              ) : (
                <>
                  {!row.followUpId ? (
                    <Button variant="outline" onClick={openFollowUpForm}>
                      {t('detail.assignFollowUp')}
                    </Button>
                  ) : null}
                  {row.convertedMemberId ? (
                    <Button asChild>
                      <Link to={`/people/${row.convertedMemberId}`}>{t('detail.viewInPeople')}</Link>
                    </Button>
                  ) : null}
                </>
              )}
            </CardFooter>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
